import { useEffect, useState } from "react";
import { Loader2, GripVertical } from "lucide-react";
import { listSheetsAndTables, readHeaders, readDataTable, SheetMeta } from "../lib/office";
import { STORAGE_SHEET_NAME } from "../lib/storage";
import { AppConfig } from "../types/state";

interface Props {
  initial?: AppConfig;
  onCancel?: () => void;
  onValidate: (config: AppConfig, initialRows: Record<string, string>[], initialColumns: string[]) => Promise<void>;
  submitLabel?: string;
  title?: string;
}

export function ConfigScreen({ initial, onCancel, onValidate, submitLabel, title }: Props) {
  const [sheets, setSheets] = useState<SheetMeta[] | null>(null);
  const [sheet, setSheet] = useState(initial?.dataSheetName ?? "");
  const [table, setTable] = useState<string | "">(initial?.dataTableName ?? "");
  const [headerRowCount, setHeaderRowCount] = useState(initial?.headerRowCount ?? 1);
  const [topRowsToSkip, setTopRowsToSkip] = useState(initial?.topRowsToSkip ?? 0);
  const [headers, setHeaders] = useState<string[]>([]);
  const [keyColumns, setKeyColumns] = useState<string[]>(initial?.keyColumns ?? []);
  const [ignore, setIgnore] = useState<string[]>(initial?.columnsToIgnore ?? []);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    listSheetsAndTables(STORAGE_SHEET_NAME)
      .then(setSheets)
      .catch((e) => setErr(String(e)));
  }, []);

  useEffect(() => {
    if (!sheet) {
      setHeaders([]);
      return;
    }
    readHeaders(sheet, table || null, headerRowCount, topRowsToSkip)
      .then((h) => {
        const filtered = h.filter(Boolean);
        setHeaders(filtered);
        setKeyColumns((prev) => prev.filter((c) => filtered.includes(c)));
        setIgnore((prev) => prev.filter((c) => filtered.includes(c)));
      })
      .catch((e) => setErr(String(e)));
  }, [sheet, table, headerRowCount, topRowsToSkip]);

  const availableTables = sheets?.find((s) => s.name === sheet)?.tables ?? [];

  const toggleIgnore = (col: string) => {
    setIgnore((prev) => (prev.includes(col) ? prev.filter((c) => c !== col) : [...prev, col]));
  };

  const toggleKey = (col: string) => {
    setKeyColumns((prev) => (prev.includes(col) ? prev.filter((c) => c !== col) : [...prev, col]));
  };

  const moveKey = (idx: number, dir: -1 | 1) => {
    setKeyColumns((prev) => {
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const submit = async () => {
    setErr(null);
    if (!sheet) return setErr("Choisis une feuille.");
    if (keyColumns.length === 0) return setErr("Choisis au moins une colonne identifiant.");
    setBusy(true);
    try {
      const data = await readDataTable(sheet, table || null, ignore, headerRowCount, topRowsToSkip);
      const config: AppConfig = {
        dataSheetName: sheet,
        dataTableName: table || null,
        keyColumns,
        headerRowCount,
        topRowsToSkip,
        columnsToIgnore: ignore,
        lastOpenedAt: new Date().toISOString(),
      };
      await onValidate(config, data.rows, data.columns);
    } catch (e) {
      setErr(String((e as Error).message ?? e));
    } finally {
      setBusy(false);
    }
  };

  if (!sheets) {
    return (
      <div className="p-6 flex items-center gap-2 text-sm text-stone-600">
        <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
      </div>
    );
  }

  const nonKeyHeaders = headers.filter((h) => !keyColumns.includes(h));

  return (
    <div className="p-4 space-y-4">
      <h1 className="font-serif-title text-lg text-stone-800">{title ?? "Configurer la liste"}</h1>

      <div className="space-y-1">
        <label className="text-xs text-stone-600">Feuille de données</label>
        <select
          className="w-full border border-stone-300 rounded px-2 py-1.5 text-sm bg-white"
          value={sheet}
          onChange={(e) => {
            setSheet(e.target.value);
            setTable("");
          }}
        >
          <option value="">Sélectionner…</option>
          {sheets.map((s) => (
            <option key={s.name} value={s.name}>
              {s.name}
            </option>
          ))}
        </select>
      </div>

      {availableTables.length > 0 && (
        <div className="space-y-1">
          <label className="text-xs text-stone-600">Tableau Excel (optionnel)</label>
          <select
            className="w-full border border-stone-300 rounded px-2 py-1.5 text-sm bg-white"
            value={table}
            onChange={(e) => setTable(e.target.value)}
          >
            <option value="">Plage utilisée complète</option>
            {availableTables.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <label className="text-xs text-stone-600">Lignes à ignorer en haut</label>
          <input
            type="number"
            min={0}
            max={50}
            className="w-full border border-stone-300 rounded px-2 py-1.5 text-sm bg-white"
            value={topRowsToSkip}
            onChange={(e) => setTopRowsToSkip(Math.max(0, Number(e.target.value) || 0))}
            disabled={!!table}
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-stone-600">Lignes d'en-tête</label>
          <input
            type="number"
            min={1}
            max={5}
            className="w-full border border-stone-300 rounded px-2 py-1.5 text-sm bg-white"
            value={headerRowCount}
            onChange={(e) => setHeaderRowCount(Math.max(1, Number(e.target.value) || 1))}
          />
        </div>
      </div>
      <p className="text-xs text-stone-500 -mt-2">
        Ignorer = nombre de lignes avant l'en-tête (titre, légende…). En-tête = nombre de lignes formant les noms de colonnes (concaténées par « / » si 2+).
        {table && " Inutile pour un tableau Excel : les bornes sont définies par le tableau."}
      </p>

      <div className="space-y-1">
        <label className="text-xs text-stone-600">
          Colonnes identifiant {keyColumns.length > 0 && <span className="text-stone-400">({keyColumns.length} sélectionnée{keyColumns.length > 1 ? "s" : ""})</span>}
        </label>
        <div className="space-y-1 max-h-40 overflow-y-auto border border-stone-200 rounded p-2 bg-white">
          {headers.length === 0 && <div className="text-xs text-stone-500">Choisis une feuille d'abord.</div>}
          {headers.map((h) => (
            <label key={h} className="flex items-center gap-2 text-sm text-stone-700">
              <input
                type="checkbox"
                checked={keyColumns.includes(h)}
                onChange={() => toggleKey(h)}
              />
              {h}
            </label>
          ))}
        </div>
        {keyColumns.length > 1 && (
          <div className="border border-stone-200 rounded p-2 bg-stone-50 mt-2 space-y-1">
            <p className="text-xs text-stone-600">Ordre dans la clé (réorganiser) :</p>
            {keyColumns.map((c, i) => (
              <div key={c} className="flex items-center gap-2 text-sm text-stone-700">
                <GripVertical className="w-3 h-3 text-stone-400" />
                <span className="flex-1">{c}</span>
                <button
                  className="text-xs text-stone-500 hover:text-stone-800 disabled:opacity-30"
                  onClick={() => moveKey(i, -1)}
                  disabled={i === 0}
                  type="button"
                >
                  ↑
                </button>
                <button
                  className="text-xs text-stone-500 hover:text-stone-800 disabled:opacity-30"
                  onClick={() => moveKey(i, 1)}
                  disabled={i === keyColumns.length - 1}
                  type="button"
                >
                  ↓
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {nonKeyHeaders.length > 0 && (
        <div className="space-y-1">
          <label className="text-xs text-stone-600">Colonnes à ignorer</label>
          <div className="space-y-1 max-h-40 overflow-y-auto border border-stone-200 rounded p-2 bg-white">
            {nonKeyHeaders.map((h) => (
              <label key={h} className="flex items-center gap-2 text-sm text-stone-700">
                <input
                  type="checkbox"
                  checked={ignore.includes(h)}
                  onChange={() => toggleIgnore(h)}
                />
                {h}
              </label>
            ))}
          </div>
        </div>
      )}

      {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">{err}</div>}

      <div className="flex gap-2 pt-2">
        {onCancel && (
          <button
            className="flex-1 border border-stone-300 rounded px-3 py-2 text-sm bg-white hover:bg-stone-50"
            onClick={onCancel}
            disabled={busy}
          >
            Annuler
          </button>
        )}
        <button
          className="flex-1 bg-stone-800 text-white rounded px-3 py-2 text-sm hover:bg-stone-700 disabled:opacity-50 flex items-center justify-center gap-2"
          onClick={submit}
          disabled={busy || !sheet || keyColumns.length === 0}
        >
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          {submitLabel ?? "Créer la version initiale"}
        </button>
      </div>
    </div>
  );
}
